class ProductImageSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.currentSlide = 0;
    this.totalSlides = 0;
  }

  connectedCallback() {
    this.#initializeSlider();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }

  #initializeSlider() {
    const swiperContainer = this.querySelector('.product-slider__swiper');
    const slides = this.querySelectorAll('.product-slider__slide');
    const progressBar = this.querySelector('.product-slider__progress-fill');
    
    if (!swiperContainer || slides.length <= 1) return;

    this.totalSlides = slides.length;

    // Import Swiper if not already available
    if (typeof Swiper === 'undefined') {
      this.#loadSwiper();
      return;
    }

    this.#createSwiper(swiperContainer, progressBar);
  }

  #loadSwiper() {
    // Load Swiper CSS
    if (!document.querySelector('link[href*="swiper"]')) {
      const swiperCSS = document.createElement('link');
      swiperCSS.rel = 'stylesheet';
      swiperCSS.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      document.head.appendChild(swiperCSS);
    }

    // Load Swiper JS
    const swiperScript = document.createElement('script');
    swiperScript.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    swiperScript.onload = () => {
      const swiperContainer = this.querySelector('.product-slider__swiper');
      const progressBar = this.querySelector('.product-slider__progress-fill');
      this.#createSwiper(swiperContainer, progressBar);
    };
    document.head.appendChild(swiperScript);
  }

  #createSwiper(swiperContainer, progressBar) {
    const prevButton = this.querySelector('.product-slider__navigation--prev .product-slider__nav-button');
    const nextButton = this.querySelector('.product-slider__navigation--next .product-slider__nav-button');

    this.swiper = new Swiper(swiperContainer, {
      loop: this.totalSlides > 1,
      autoHeight: false,
      grabCursor: true,
      observer: true,
      observeParents: true,
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
      },
      on: {
        slideChange: (swiper) => {
          this.currentSlide = swiper.realIndex;
          this.#updateProgress(progressBar);
          this.#updateNavigationVisibility();
        },
        init: () => {
          this.#updateNavigationVisibility();
        }
      }
    });

    this.#updateProgress(progressBar);
  }

  #updateProgress(progressBar) {
    if (!progressBar || this.totalSlides <= 1) return;
    
    const progress = ((this.currentSlide + 1) / this.totalSlides) * 100;
    progressBar.style.width = `${progress}%`;
  }

  #updateNavigationVisibility() {
    if (this.totalSlides <= 1) return;

    const prevNavigation = this.querySelector('.product-slider__navigation--prev');
    const nextNavigation = this.querySelector('.product-slider__navigation--next');

    if (this.currentSlide === 0) {
      prevNavigation?.classList.remove('product-slider__navigation--visible');
    } else {
      prevNavigation?.classList.add('product-slider__navigation--visible');
    }
    if (this.currentSlide === this.totalSlides - 1) {
      nextNavigation?.classList.remove('product-slider__navigation--visible');
    } else {
      nextNavigation?.classList.add('product-slider__navigation--visible');
    }
  }

  #setupEventListeners() {
    // Reset slider when filters are applied (collection page reloads)
    document.addEventListener('collection:updated', () => {
      this.#resetSlider();
    });

    // Handle touch events for mobile
    this.addEventListener('touchstart', this.#handleTouchStart.bind(this));
  }

  #handleTouchStart(e) {
    // Prevent parent touch events from interfering
    e.stopPropagation();
  }

  #resetSlider() {
    if (this.swiper) {
      this.swiper.slideTo(0, 0);
      this.currentSlide = 0;
      const progressBar = this.querySelector('.product-slider__progress-fill');
      this.#updateProgress(progressBar);
      this.#updateNavigationVisibility();
    }
  }
}

customElements.define('product-image-slider', ProductImageSlider);
